/**
 * Skeletons reusables para los loaders iniciales de páginas.
 *
 * Cada función arma un layout con `<Skeleton>` primitives que aproxima el
 * shape final (filas tabulares, grid de cards, dashboard de números). Las
 * páginas eligen el variant que mejor refleje su contenido para que la
 * transición skeleton → datos no produzca jumps de layout.
 */

import { Card, CardContent, Skeleton } from '@atreus/ui';

/**
 * Convención de padding: ninguno de los helpers aplica padding al outer
 * div. El caller lo envuelve en su contenedor con el padding que
 * corresponda al layout (típicamente `p-8`). Esto evita sumar paddings
 * cuando la página ya tiene un wrapper scrollable propio.
 */

/** Lista vertical de filas — Categories, Permisos, Pedidos (list view). */
export const RowsSkeleton = ({
  count = 8,
  rowClassName = 'h-14',
}: {
  count?: number;
  rowClassName?: string;
}) => (
  <div className="space-y-3">
    {Array.from({ length: count }).map((_, i) => (
      <Skeleton key={i} className={`w-full ${rowClassName}`} />
    ))}
  </div>
);

/** Grid responsive de cards — Products, Pedidos (grid view), Inventory. */
export const CardsSkeleton = ({
  count = 9,
  withImage = true,
}: {
  count?: number;
  withImage?: boolean;
}) => (
  <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
    {Array.from({ length: count }).map((_, i) => (
      <Card key={i}>
        <CardContent className="space-y-3 p-4 pt-4">
          {withImage ? <Skeleton className="aspect-square w-full" /> : null}
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </CardContent>
      </Card>
    ))}
  </div>
);

/** Dashboard de cards con números grandes — Stats, Cash. */
export const StatCardsSkeleton = ({ count = 4 }: { count?: number }) => (
  <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
    {Array.from({ length: count }).map((_, i) => (
      <Card key={i}>
        <CardContent className="space-y-2 p-4 pt-4">
          <Skeleton className="h-3 w-1/3" />
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-3 w-1/4" />
        </CardContent>
      </Card>
    ))}
  </div>
);

/** Grid cuadrado para Mesas — cuadrados ≈ 1:1 con label encima. */
export const TablesGridSkeleton = ({ count = 12 }: { count?: number }) => (
  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
    {Array.from({ length: count }).map((_, i) => (
      <Skeleton key={i} className="aspect-square w-full" />
    ))}
  </div>
);

/** Matriz tabular — Permissions. Header + N filas con N celdas. */
export const MatrixSkeleton = ({
  rows = 8,
  cols = 5,
}: {
  rows?: number;
  cols?: number;
}) => (
  <div className="space-y-2 p-2">
    <div className="grid gap-2" style={{ gridTemplateColumns: `2fr repeat(${cols}, 1fr)` }}>
      <Skeleton className="h-6 w-full" />
      {Array.from({ length: cols }).map((_, i) => (
        <Skeleton key={i} className="h-6 w-full" />
      ))}
    </div>
    {Array.from({ length: rows }).map((_, r) => (
      <div
        key={r}
        className="grid gap-2"
        style={{ gridTemplateColumns: `2fr repeat(${cols}, 1fr)` }}
      >
        <Skeleton className="h-8 w-full" />
        {Array.from({ length: cols }).map((_, c) => (
          <Skeleton key={c} className="h-8 w-full" />
        ))}
      </div>
    ))}
  </div>
);
